import { getAuthToken, API_CONFIG } from '../config/api.config';
import type { Slide, SlidesResponse, CreateSlideRequest, UpdateSlideRequest } from '../../domain/entities/Slide';

export class SlideRepository {
  // URL основного API (где хранятся слайды) - берется из конфигурации
  private get SHOP_API_URL(): string {
    return `${API_CONFIG.shopApiUrl}/api`;
  }
  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  }

  async getSlides(activeOnly: boolean = false): Promise<Slide[]> {
    const queryParams = activeOnly ? '?active=true' : '';
    const response = await fetch(`${this.SHOP_API_URL}/slides${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch slides: ${response.statusText}`);
    }
    
    const data: SlidesResponse = await response.json();
    return data.slides;
  }

  async getSlide(id: number): Promise<Slide> {
    const response = await fetch(`${this.SHOP_API_URL}/slides/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Slide not found');
      }
      throw new Error(`Failed to fetch slide: ${response.statusText}`);
    }
    
    return await response.json();
  }

  async createSlide(data: CreateSlideRequest): Promise<Slide> {
    const response = await this.fetchWithAuth(`${this.SHOP_API_URL}/slides`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create slide: ${errorText}`);
    }

    return await response.json();
  }

  async updateSlide(id: number, data: UpdateSlideRequest): Promise<Slide> {
    const response = await this.fetchWithAuth(`${this.SHOP_API_URL}/slides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update slide: ${errorText}`);
    }

    return await response.json();
  }

  async deleteSlide(id: number): Promise<void> {
    const response = await this.fetchWithAuth(`${this.SHOP_API_URL}/slides/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete slide: ${errorText}`);
    }
  }

  async reorderSlides(slides: Array<{ id: number; order: number }>): Promise<void> {
    const response = await this.fetchWithAuth(`${this.SHOP_API_URL}/slides/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ slides }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to reorder slides: ${errorText}`);
    }
  }
}

export const slideRepository = new SlideRepository();

